import React, { useState, useEffect } from 'react';
import { XMarkIcon, DocumentTextIcon, CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { templateService } from '../services/api';

const WABATemplatePreviewDialog = ({ isOpen, onClose, clid, templateName }) => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && clid && templateName) {
      fetchTemplate();
    }
  }, [isOpen, clid, templateName]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await templateService.getTemplateByClidAndName(clid, templateName);
      setTemplate(response.data);
    } catch (err) {
      console.error('Error fetching template:', err);
      setError('Failed to load template details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTemplate(null);
    setError(null);
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'rejected':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'pending':
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderComponent = (component, index) => {
    if (!component) return null;

    return (
      <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-900 capitalize">
            {component.type || 'Component'}
          </h4>
          {component.format && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {component.format}
            </span>
          )}
        </div>

        {component.text && (
          <div className="mb-3">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {component.text}
            </p>
          </div>
        )}

        {component.example && (
          <div className="bg-gray-50 p-3 rounded">
            <p className="text-xs text-gray-600 mb-1">Example:</p>
            {component.example.body_text_named_params ? (
              <div className="space-y-2">
                {component.example.body_text_named_params.map((param, paramIndex) => (
                  <div key={paramIndex} className="text-sm text-gray-800">
                    <span className="font-mono text-blue-600">{`{{${param.param_name}}}`}</span>
                    <span className="text-gray-600 ml-2">→</span>
                    <span className="ml-2">{param.example}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-800 font-mono">
                {component.example.body?.text || JSON.stringify(component.example)}
              </p>
            )}
          </div>
        )}

        {component.buttons && component.buttons.length > 0 && (
          <div className="mt-3">
            <p className="text-xs text-gray-600 mb-2">Buttons:</p>
            <div className="space-y-1">
              {component.buttons.map((button, btnIndex) => (
                <div key={btnIndex} className="text-sm text-blue-600 hover:text-blue-800">
                  {button.text || button.title}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Template Details
              </h2>
              <p className="text-sm text-gray-500">
                {templateName}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading template details...</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <XCircleIcon className="w-5 h-5 text-red-400 mr-2" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          ) : template ? (
            <div className="space-y-6">
              {/* Template Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template ID
                  </label>
                  <p className="text-sm text-gray-900 font-mono">
                    {template.id || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <div className="flex items-center">
                    {getStatusIcon(template.status)}
                    <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(template.status)}`}>
                      {template.status || 'Unknown'}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <p className="text-sm text-gray-900">
                    {template.category || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Language
                  </label>
                  <p className="text-sm text-gray-900">
                    {template.language || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Template Components */}
              {template.components && template.components.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Template Components
                  </h3>
                  <div className="space-y-4">
                    {template.components.map((component, index) => renderComponent(component, index))}
                  </div>
                </div>
              )}

              {/* Raw Template Data (for debugging) */}
              <details className="border border-gray-200 rounded-lg">
                <summary className="p-4 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Raw Template Data
                </summary>
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                  <pre className="text-xs text-gray-600 overflow-x-auto">
                    {JSON.stringify(template, null, 2)}
                  </pre>
                </div>
              </details>
            </div>
          ) : (
            <div className="text-center py-12">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No template data</h3>
              <p className="mt-1 text-sm text-gray-500">
                Template information could not be loaded.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default WABATemplatePreviewDialog;
